import { NO_STORE_POG_TRIGGER } from "@CommonApi/merchflow/_merchflowId_/store-pog-trigger/all-finished";
import { useAtom, useSetAtom } from "jotai";
import { useEffect, useRef, useState, useMemo } from "react";
import { useParams } from "react-router-dom";

import { Page, Planogram, PogAgentChat } from "src/components";
import { useIsPogAgentEnabled } from "src/components/PogAgentChat/store/hooks";
import { useTranslation } from "src/i18n";
import { useLoaders } from "src/components/InlineLoader";
import { useModals } from "src/components/Modals";
import { Button, Color, Flex, Spinner, Text, Icon } from "src/elements";
import { useModifiedPog } from "src/hooks/useModifiedPogAtom";
import { useUnsavedChangesGuard } from "src/hooks/useUnsavedChangesGuard";
import { MerchflowHeader } from "../../components/merchflows/MerchflowHeader/MerchflowHeader";
import { useMerchflowHeader } from "../../components/merchflows/MerchflowHeader/store/hooks";
import { MerchflowStepsNavigator } from "../../components/merchflows/MerchflowStepsNavigator/MerchflowStepsNavigator";
import {
  merchflows_merchflowId_Stores_storeCode_Pogs_storePogId_Atom,
  merchflows_merchflowId_Stores_storeCode_Pogs_storePogId_PogAtom,
} from "./store/atoms";
import { useNavigation } from "src/utils/navigation";
import { pages } from "src/utils";
import { highlightsModeAtom } from "src/components/Planogram/store/atoms";
import { PlanogramMetrics } from "src/components/PlanogramExtensions/PlanogramMetrics";
import { PlanogramHovererDetails } from "src/components/PlanogramExtensions/PlanogramHovererDetails/PlanogramHovererDetails";
import { RoutePutMerchflowStorePog_storePogId_ } from "@CommonApi/merchflow/store/pog/_storePogId_";
import { useApi } from "src/api";
import { RoutePutMerchflows_MerchflowId_StoresApprovals } from "@CommonApi/merchflows/_merchflowId_/stores/approvals";
import { useCreateTemplateModal } from "src/modals/CreateTemplate/hooks";
import { PlanogramLegend } from "src/components/PlanogramExtensions/PlanogramLegend";
import { StorePogLogsButton } from "./components/StorePogLogsButton";
import { routeToUrl } from "@CommonUtils/routes";
import { PlanogramToolbar } from "src/components/Planogram/components/Toolbar/PlanogramToolbar";
import {
  PayloadPostMerchflows_merchflowId_ExportsBatch,
  ResponsePostMerchflows_merchflowId_ExportsBatch,
  RoutePostMerchflows_merchflowId_ExportsBatch,
} from "@CommonApi/merchflows/_merchflowId_/exports/batch";
import { usePogCsvColumnsModal } from "src/modals/PogCsvColumns/store/hooks";
import { useStorePogNotesModal } from "src/modals/StorePogNotes/store/hooks";
import { useStorePogNotesQuery } from "src/api/merchflows/storePogNotes";
import { hasPermission, PermissionCode } from "@CommonUtils/RolesPermissions";
import { useUser } from "src/atoms/user";
import { ResponseGetMerchflows_merchflowId_StoresAll } from "@CommonApi/merchflows/_merchflowId_/stores/all";
import { PdfExportBatchStatus } from "src/components/PdfExportBatchStatus/PdfExportBatchStatus";
import { SplitPlanSummary } from "src/components/SplitPlanSummary/SplitPlanSummary";
import { RelaxedToFinish } from "src/components/RelaxedToFinish/RelaxedToFinish";
import { fallbackRelaxations } from "src/utils/pogWarning";
import { splitPlanNamesOf } from "src/utils/describeSplitPlan";

interface StoreNavigatorProps {
  merchflowId: number;
  storeCode: string;
  isModified: boolean;
  /** Run of the POG on screen; undefined until it loads. 0 means the run has no trigger. */
  storePogTriggerId: number | null | undefined;
}

const StoreNavigator = ({
  merchflowId,
  storeCode,
  isModified,
  storePogTriggerId,
}: StoreNavigatorProps) => {
  const { getMerchflowStoresAllApi } = useApi();
  const { navigate } = useNavigation();
  const [storesAllData, setStoresAllData] =
    useState<ResponseGetMerchflows_merchflowId_StoresAll | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Wait for the POG so the neighbours come from its run. Without the filter
    // the backend falls back to the latest version and prev/next jump runs.
    if (storePogTriggerId === undefined) {
      return;
    }

    let isMounted = true;
    (async () => {
      setIsLoading(true);
      try {
        const response = await getMerchflowStoresAllApi(merchflowId, {
          page: 1,
          size: 100000,
          viewMode: "list",
          store_pog_trigger_id: storePogTriggerId ?? NO_STORE_POG_TRIGGER,
        });
        if (isMounted) {
          setStoresAllData(response);
        }
      } catch (error) {
        console.error("Failed to load stores sequence", error);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [merchflowId, storePogTriggerId]);

  const storesList = useMemo(() => storesAllData?.data ?? [], [storesAllData]);
  const currentStoreIndex = useMemo(
    () => storesList.findIndex((store) => store.store === storeCode),
    [storesList, storeCode],
  );

  const navigateToStore = (targetStoreCode: string, targetStorePogId: number) => {
    navigate(
      routeToUrl(pages.merchflows_merchflowId_Stores_storeCode_Pogs_storePogId_, {
        merchflowId,
        storeCode: targetStoreCode,
        storePogId: targetStorePogId,
      }),
    );
  };

  const onPrevStore = () => {
    if (currentStoreIndex > 0) {
      const prevStore = storesList[currentStoreIndex - 1];
      navigateToStore(prevStore.store, prevStore.store_pog?.store_pog_id ?? 0);
    }
  };

  const onNextStore = () => {
    if (currentStoreIndex >= 0 && currentStoreIndex < storesList.length - 1) {
      const nextStore = storesList[currentStoreIndex + 1];
      navigateToStore(nextStore.store, nextStore.store_pog?.store_pog_id ?? 0);
    }
  };

  if (isLoading || storesList.length <= 1) {
    return null;
  }

  return (
    <Flex justify="right" align="center" gap="10px">
      <Button
        onClick={onPrevStore}
        variant="borderless"
        isDisabled={currentStoreIndex <= 0 || isModified}
        iconLeft={{ name: "arrowLeft" }}
      >
        Prev
      </Button>

      <Text variant="small1" color={Color.textSecondary}>
        Store {currentStoreIndex + 1} of {storesList.length}
      </Text>

      <Button
        onClick={onNextStore}
        variant="borderless"
        isDisabled={
          currentStoreIndex === -1 || currentStoreIndex >= storesList.length - 1 || isModified
        }
        iconRight={{ name: "arrowRight" }}
      >
        Next
      </Button>
    </Flex>
  );
};

export const PageMerchflows_merchflowId_Stores_storeCode_Pogs_storePogId_ = () => {
  const {
    merchflowId: _merchflowId,
    storePogId: _storePogId,
    storeCode: _storeCode,
  } = useParams<{
    merchflowId: string;
    storeCode: string;
    storePogId: string;
  }>();
  const merchflowId = Number(_merchflowId);
  const storeCode = String(_storeCode);
  const storePogId = Number(_storePogId);

  const { getMerchflowStorePogApi, putMerchflowStorePogApi, postExportBatchPogsApi } = useApi();
  const { openPogCsvColumnsModal } = usePogCsvColumnsModal();
  const { putMerchflowApprovalsApi } = useApi();
  const { hasLoader } = useLoaders();
  const { openConfirmModal } = useModals();
  const { openCreateTemplateModal } = useCreateTemplateModal();
  const { navigate } = useNavigation();
  const { user } = useUser();
  const { merchflowDetails } = useMerchflowHeader();
  const canCreate = hasPermission(user.roleCode, PermissionCode.MERCHFLOW_CREATE);
  const canEditNotes = hasPermission(user.roleCode, PermissionCode.MERCHFLOW_UPDATE);
  const { openStorePogNotesModal } = useStorePogNotesModal();
  const { data: storePogNotes } = useStorePogNotesQuery(storePogId);

  const [data, setData] = useAtom(merchflows_merchflowId_Stores_storeCode_Pogs_storePogId_Atom);
  const dataRef = useRef(data);
  const [isApprovalDone, setIsApprovalDone] = useState(false);
  const [isSavingDone, setIsSavingDone] = useState(false);
  const [isPlanogramLoading, setIsPlanogramLoading] = useState(false);
  const [isAgentOpen, setIsAgentOpen] = useState(false);
  /** The canvas is drawing a POG agent preview rather than the merchandiser's own edits. */
  const [isShowingAgentPreview, setIsShowingAgentPreview] = useState(false);
  /** Counts saves of this POG, so the agent panel can tell an older preview is stale. */
  const [savedPogRevision, setSavedPogRevision] = useState(0);
  /**
   * The save revision at which the agent's preview was saved as-is, so this
   * store already holds the plan. Null when the last save was anything else.
   */
  const [savedAgentPreviewRevision, setSavedAgentPreviewRevision] = useState<number | null>(null);
  /** A write is out to the agent; the canvas and Save stay locked until it lands. */
  const [isAgentApplying, setIsAgentApplying] = useState(false);
  const { t: tAgent } = useTranslation("pogAgent");
  const isPogAgentEnabled = useIsPogAgentEnabled();
  const { t } = useTranslation("common");

  const isPogAvailable = data?.status === "DONE";
  const isSaveLoading = hasLoader(RoutePutMerchflowStorePog_storePogId_);
  const isExportLoading = hasLoader(RoutePostMerchflows_merchflowId_ExportsBatch);
  const [pdfExportBatch, setPdfExportBatch] =
    useState<ResponsePostMerchflows_merchflowId_ExportsBatch>();
  const isMounted = useRef(true);
  const activeMerchflowIdRef = useRef(merchflowId);
  const isApprovalLoading = hasLoader(RoutePutMerchflows_MerchflowId_StoresApprovals);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    activeMerchflowIdRef.current = merchflowId;
    setPdfExportBatch(undefined);
  }, [merchflowId, storePogId]);

  const {
    setPog: setStorePog,
    setPogAsUnsaved: setStorePogAsUnsaved,
    hasEditsSinceUnsavedPog,
    pog: storePog,
    revertPog,
    isModified,
    savePogAsOriginal,
  } = useModifiedPog(merchflows_merchflowId_Stores_storeCode_Pogs_storePogId_PogAtom);

  // Saving, reverting, or reloading all leave the canvas showing something the
  // agent did not put there, so the preview flag follows the modified flag down.
  useEffect(() => {
    if (!isModified) {
      setIsShowingAgentPreview(false);
    }
  }, [isModified]);

  /**
   * Gate every agent action that would redraw the canvas. The agent works from
   * the POG saved in OWA, so it cannot see hand edits and its result would
   * silently replace them. Checked here, at the moment of the action, because it
   * compares whole planograms.
   */
  const confirmCanvasOverwrite = (): Promise<boolean> => {
    if (!hasEditsSinceUnsavedPog()) {
      return Promise.resolve(true);
    }

    // Unresolved when the modal is dismissed another way, which leaves the
    // action undone: the safe outcome.
    return new Promise((resolve) => {
      openConfirmModal({
        title: tAgent("page.replaceTitle"),
        description: tAgent("page.replaceDescription"),
        icon: { name: "alert", color: Color.yellow },
        buttons: [
          {
            id: "keep-my-changes",
            label: tAgent("page.keepMyChanges"),
            color: "primary",
            variant: "inverted",
            onClick: () => resolve(false),
          },
          {
            id: "replace-planogram",
            label: tAgent("page.replace"),
            color: "red",
            onClick: () => resolve(true),
          },
        ],
      });
    });
  };

  useUnsavedChangesGuard(isModified, { onSave: () => onSave(false) });

  const activeStorePogIdRef = useRef<number>(storePogId);
  const approvalTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const loadStorePog = async (id: number) => {
    try {
      const response = await getMerchflowStorePogApi(id);
      if (activeStorePogIdRef.current === id) {
        setData(response);
      }
    } catch (error) {
      console.error("Failed to load store planogram", error);
      if (activeStorePogIdRef.current === id) {
        setData(null);
      }
    }
  };

  useEffect(() => {
    activeStorePogIdRef.current = storePogId;
    setIsPlanogramLoading(true);
    (async () => {
      await loadStorePog(storePogId);
      setIsPlanogramLoading(false);
    })();

    // Cleanup.
    return () => {
      setData(null);
      setStorePog(null);
      if (approvalTimeoutRef.current) clearTimeout(approvalTimeoutRef.current);
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, [storePogId]);

  // Save POG to atom once.
  useEffect(() => {
    if (data?.pog && (dataRef.current === null || storePog === null)) {
      setStorePog(data.pog);
      dataRef.current = data;
    }
  }, [data]);

  // Keep loading pog data if the pog generation is ongoing.
  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null;
    if (
      data?.status === "NOT_STARTED" ||
      data?.status === "POGGER_IN_PROGRESS" ||
      data?.status === "UPDATE_IN_PROGRESS"
    ) {
      timeoutId = setTimeout(() => {
        loadStorePog(storePogId);
      }, 10_000);
    }
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [data, storePogId]);

  const exportStorePog = (type: PayloadPostMerchflows_merchflowId_ExportsBatch["type"]) => {
    if (type === "csv") {
      openPogCsvColumnsModal({
        merchflowId,
        payload: { store_pogs_ids: [storePogId], type: "csv" },
      });
      return;
    }

    if (type === "pdfV2") {
      setPdfExportBatch(undefined);
    }
    const isPdfPollingActive = () =>
      isMounted.current &&
      activeMerchflowIdRef.current === merchflowId &&
      activeStorePogIdRef.current === storePogId;
    postExportBatchPogsApi(
      merchflowId,
      {
        store_pogs_ids: [storePogId],
        type,
      },
      type === "pdfV2"
        ? (batch) => {
            if (isPdfPollingActive()) setPdfExportBatch(batch);
          }
        : undefined,
      type === "pdfV2" ? isPdfPollingActive : undefined,
    );
  };

  const onApproval = async () => {
    if (!data) return;

    if (data.is_approved === true) {
      const unapproved = await putMerchflowApprovalsApi(merchflowId, {
        unapproved: [{ store_pog_id: storePogId }],
      });

      if (unapproved) {
        setIsApprovalDone(true);

        approvalTimeoutRef.current = setTimeout(() => {
          if (activeStorePogIdRef.current === storePogId) {
            setIsApprovalDone(false);
            setData((prevData) => {
              if (!prevData) return null;
              return { ...prevData, is_approved: false };
            });
          }
        }, 1000);
      }
    } else {
      const approved = await putMerchflowApprovalsApi(merchflowId, {
        approved: [{ store_pog_id: storePogId }],
      });

      if (approved) {
        setIsApprovalDone(true);

        approvalTimeoutRef.current = setTimeout(() => {
          if (activeStorePogIdRef.current === storePogId) {
            setIsApprovalDone(false);
            setData((prevData) => {
              if (!prevData) return null;
              return { ...prevData, is_approved: true };
            });
          }
        }, 1000);
      }
    }
  };

  const onSave = async (saveAsNew: boolean): Promise<boolean> => {
    if (!storePog) return false;

    // Saving the agent's preview untouched puts this store's POG where a
    // single-POG apply would have put it, so the panel can skip straight to the
    // rest of the merchflow. Hand edits on top do not count: the merchflow would
    // roll out the agent's plan, not what was saved here.
    const isSavingAgentPreview = isShowingAgentPreview && !hasEditsSinceUnsavedPog();

    const pogToSave = storePog.toNonCircular();
    const newStorePogId = await putMerchflowStorePogApi({
      storePogId,
      payload: {
        pog: pogToSave,
        save_as_new: saveAsNew,
      },
    });

    if (!newStorePogId) return false;

    savePogAsOriginal(pogToSave);
    const revision = savedPogRevision + 1;
    setSavedPogRevision(revision);
    setSavedAgentPreviewRevision(isSavingAgentPreview ? revision : null);
    setIsSavingDone(true);
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      if (activeStorePogIdRef.current === storePogId) {
        setIsSavingDone(false);

        if (saveAsNew && newStorePogId !== storePogId) {
          navigate(
            routeToUrl(pages.merchflows_merchflowId_Stores_storeCode_Pogs_storePogId_, {
              merchflowId,
              storeCode,
              storePogId: newStorePogId,
            }),
          );
        }
      }
    }, 1000);

    return true;
  };

  /**
   * Pull the planogram back from OWA. The POG agent writes through OWA and
   * never touches the canvas, so the screen is stale until we refetch. Dropping
   * the atom first lets the load effect re-seed it from the new response.
   */
  const refreshStorePogFromOwa = async () => {
    setIsPlanogramLoading(true);
    setSavedAgentPreviewRevision(null);
    dataRef.current = null;
    setStorePog(null);
    await loadStorePog(storePogId);
    setIsPlanogramLoading(false);
  };

  const setHighlightsMode = useSetAtom(highlightsModeAtom);
  const splitPlan = data?.pog?.splitPlan;
  const relaxedRungs = useMemo(() => fallbackRelaxations(data?.pog), [data?.pog]);
  const splitPlanNames = useMemo(
    () => (splitPlan ? splitPlanNamesOf(data?.pog) : new Map<string, string>()),
    [splitPlan, data?.pog],
  );

  useEffect(() => {
    setHighlightsMode(false);
  }, []);

  return (
    <Page>
      <MerchflowHeader merchflowId={merchflowId} storeCode={storeCode} storePogId={storePogId} />

      <MerchflowStepsNavigator
        activeStep="STORES"
        merchflowId={merchflowId}
        basePogId={data?.base_pog_id}
      />

      <StoreNavigator
        merchflowId={merchflowId}
        storeCode={storeCode}
        isModified={isModified}
        storePogTriggerId={data?.store_pog_trigger_id}
      />

      <Flex minHeight="0" gap="10px" flexGrow>
        <Flex minHeight="0" minWidth="0" gap="10px" flexGrow column>
          {isPlanogramLoading && (
            <Spinner size="big" label="Store planogram is loading..." isCentered />
          )}

          {data === null && !isPlanogramLoading && (
            <Flex justify="center" align="center" column gap="10px" width="100%" height="100%">
              <Icon name="alert" color={Color.yellow} size={40} />

              <Text variant="h3" color={Color.yellow}>
                Store planogram not found
              </Text>
            </Flex>
          )}

          {data?.status === "ERROR" && (
            <Flex justify="center" align="center" column gap="10px" width="100%" height="100%">
              <Icon name="alert" color={Color.red} size={40} />

              <Text variant="h3" color={Color.red}>
                Store planogram generation failed
              </Text>
            </Flex>
          )}

          {data &&
            (data.status === "NOT_STARTED" ||
              data.status === "POGGER_IN_PROGRESS" ||
              data.status === "UPDATE_IN_PROGRESS") && (
              <Spinner size="big" label="Store planogram is being generated..." isCentered />
            )}

          {data?.status === "DONE" && storePog && (
            <>
              {relaxedRungs.length > 0 && (
                <Flex>
                  <RelaxedToFinish rungs={relaxedRungs} />
                </Flex>
              )}

              <SplitPlanSummary
                plan={splitPlan}
                cdtNames={splitPlanNames}
                brandsMayCrossAisleBreak={data.brandsMayCrossAisleBreak === true}
                variant="body2"
              />

              <PlanogramToolbar
                pogAtom={merchflows_merchflowId_Stores_storeCode_Pogs_storePogId_PogAtom}
                showCanvasRenderer
              />

              <Planogram
                merchflowId={merchflowId}
                currentStoreCode={storeCode}
                pogAtom={merchflows_merchflowId_Stores_storeCode_Pogs_storePogId_PogAtom}
                // Locked while the agent writes: an edit made in that window
                // would be replaced by the refetch without anyone being asked.
                isEditable={!isAgentApplying}
                isPanelVisible
                isPog
                isWithBorder
                allowKonvaEditor
                categoryName={merchflowDetails?.category_code}
              />

              <PlanogramLegend />

              <PlanogramHovererDetails />
            </>
          )}
        </Flex>

        {isPogAgentEnabled && isPogAvailable && (
          <PogAgentChat
            // A thread and its signed plan belong to one POG, so moving to
            // another store starts a new conversation rather than carrying the
            // old plan across. Closing the panel only hides it: a write that
            // lands while it is closed must still refresh this canvas.
            key={storePogId}
            isOpen={isAgentOpen}
            merchflowId={merchflowId}
            storeCode={storeCode}
            storePogId={storePogId}
            // The agent reads the saved POG from OWA, so a prompt sent while
            // the canvas has unsaved edits would preview against the wrong
            // layout and apply over those edits. Its own preview is exempt: it
            // was derived from that same saved POG.
            isBlocked={isModified && !isShowingAgentPreview}
            isShowingPreview={isModified && isShowingAgentPreview}
            savedPreviewRevision={savedAgentPreviewRevision}
            savedPogRevision={savedPogRevision}
            onApplyingChange={setIsAgentApplying}
            confirmCanvasOverwrite={confirmCanvasOverwrite}
            onShowPreview={(pog) => {
              setStorePogAsUnsaved(pog);
              setIsShowingAgentPreview(true);
            }}
            onPogWritten={() => void refreshStorePogFromOwa()}
            // Same destination as "Save New Version": the new store POG. The
            // panel is keyed by storePogId, so it starts a fresh thread there.
            onPogCreated={(newStorePogId) =>
              navigate(
                routeToUrl(pages.merchflows_merchflowId_Stores_storeCode_Pogs_storePogId_, {
                  merchflowId,
                  storeCode,
                  storePogId: newStorePogId,
                }),
              )
            }
            onClose={() => setIsAgentOpen(false)}
          />
        )}
      </Flex>

      <Flex justify="between" align="center">
        <Flex gap="16px" align="center" minWidth="0">
          <PdfExportBatchStatus merchflowId={merchflowId} batch={pdfExportBatch} />

          <PlanogramMetrics
            pogAtom={merchflows_merchflowId_Stores_storeCode_Pogs_storePogId_PogAtom}
            isLoading={isPlanogramLoading}
          />
        </Flex>

        <Flex gap="10px" flexShrink={0}>
          {isPogAgentEnabled && (
            <Button
              color="greenSmoke"
              variant="inverted"
              isEnabled={isPogAvailable}
              iconLeft={{ name: "ai" }}
              ariaExpanded={isAgentOpen}
              onClick={() => setIsAgentOpen(!isAgentOpen)}
            >
              {tAgent("page.openAgent")}
            </Button>
          )}

          <Button
            color="greenSmoke"
            variant="inverted"
            iconLeft={storePogNotes?.notes ? { name: "info" } : undefined}
            onClick={() => openStorePogNotesModal({ storePogId, canEdit: canEditNotes })}
          >
            {t("storePogNotes.button")}
          </Button>

          {isModified && (
            <>
              <Button
                color="red"
                variant="inverted"
                isDisabled={!isPogAvailable || isSaveLoading || isSavingDone || isAgentApplying}
                onClick={revertPog}
              >
                Revert Changes
              </Button>

              <Button
                isEnabled={isPogAvailable && !isAgentApplying}
                isLoading={isSaveLoading}
                isSuccessful={isSavingDone}
                onClick={() => onSave(false)}
                dropdown={[
                  {
                    label: "Save New Version",
                    onClick: () => onSave(true),
                  },
                ]}
              >
                Save
              </Button>
            </>
          )}

          {!isModified && (
            <>
              <StorePogLogsButton
                merchflowId={merchflowId}
                storeCode={storeCode}
                storePogId={storePogId}
              />

              <Button
                color="greenSmoke"
                variant="inverted"
                isEnabled={isPogAvailable}
                onClick={() => navigate(routeToUrl(pages.storeViewer_storePogId_, { storePogId }))}
              >
                Open viewer
              </Button>

              <Button
                color="greenSmoke"
                isEnabled={isPogAvailable}
                isLoading={isExportLoading}
                variant="inverted"
                onClick={() => exportStorePog("csv")}
                dropdown={[
                  {
                    label: "Export CSV",
                    onClick: () => exportStorePog("csv"),
                  },
                  {
                    label: "Export PDF",
                    onClick: () => exportStorePog("pdfV2"),
                  },
                  {
                    label: "Export PSA",
                    onClick: () => exportStorePog("psaV2"),
                  },
                ]}
              >
                Export
              </Button>

              {canCreate && (
                <Button
                  color="greenSmoke"
                  isEnabled={isPogAvailable}
                  variant="inverted"
                  onClick={() => openCreateTemplateModal({ storeCode, storePogId })}
                >
                  Create Template
                </Button>
              )}

              <Button
                color="primary"
                onClick={onApproval}
                isEnabled={isPogAvailable}
                isLoading={isApprovalLoading}
                isSuccessful={isApprovalDone}
              >
                {data?.is_approved ? "Unapprove" : "Approve"}
              </Button>
            </>
          )}
        </Flex>
      </Flex>
    </Page>
  );
};
